/**
 * tableClipboard.ts - Pasting into the table. Private to `createTableInteractions`.
 */

import {assert} from 'Asserts';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {TableWYCIWYPPart} from 'notebook/mediocre/clipboard/WyciwypConstants';
import {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {parseRawDataToCells} from 'notebook/tables/store/logic/interactions/tableInteractionLogic';
import {compareTableIndex, TableSelection} from 'notebook/tables/store/logic/interactions/tableSelection';
import {computeIsRealIndex} from 'notebook/tables/store/logic/TableSelectionLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import {ClipboardItemType} from 'util/clipboard/ClipboardUtil';
import {ElementClipboardEvent} from 'util/Types';

export interface TablePasteOptions {
  transpose?: boolean;
  type: ClipboardItemType;
  selectionIndex?: TableIndex;
  /** When true, pasted values replace cell content but destination formatting is kept. */
  preserveDestinationFormatting?: boolean;
}

export interface TableClipboard {
  /** Pastes the event's clipboard data, preventing the browser's default paste when it handles it. */
  paste: (e: ElementClipboardEvent, options?: TablePasteOptions) => void;
}

export function createTableClipboard({
  selection: tableSelection,
  getTableModel,
  getTableProps,
  isMounted,
  finishEditorAfterEdit,
}: {
  selection: TableSelection;
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
  isMounted: () => boolean;
  finishEditorAfterEdit: () => void;
}): TableClipboard {
  /** The top left cell of the merged region containing `index`, or `index` when it is not merged. */
  const findUnmergedCell = (index: TableIndex): TableIndex => {
    const {table, computedTable} = getTableProps();
    if (!computeIsRealIndex(index, table)) {
      return index;
    }
    const mergedBox = computedTable[index[0]][index[1]]?.mergedBox;
    return mergedBox != null ? [mergedBox.minI, mergedBox.minJ] : index;
  };

  /** Commits `text` and `cellData` to the cell at `index`, leaving that cell selected. */
  const commitCellAndStay = (index: TableIndex, text: string, cellData: Partial<Cell>): void => {
    const {mutations} = getTableModel();
    mutations.saveSelection();
    mutations.updateCell(index, {text, ...cellData});

    const {selection, state} = getTableModel();
    const collapse =
      !selection.hasRealSelection ||
      compareTableIndex(state.multiStartIndex as TableIndex, state.multiEndIndex as TableIndex) === 0;
    const newIndex = findUnmergedCell(index);
    tableSelection.select(newIndex, {
      editing: false,
      collapse,
      text: compareTableIndex(newIndex, index) === 0 ? text : null,
      multiActualIndex: index,
    });
    finishEditorAfterEdit();
  };

  const canAttachFileToSelectedCell = (): boolean => {
    const index = getTableModel().state.multiSelIndex;
    return Boolean(index && getTableProps().canAttachFileToCell?.(index));
  };

  const attachFileToSelectedCell = async (file: File): Promise<void> => {
    if (!canAttachFileToSelectedCell()) {
      return;
    }
    // Capture the index before uploading, in case the selection changes meanwhile.
    const index = getTableModel().state.multiSelIndex!;
    const {convertFileToCellData} = getTableProps();
    assert(convertFileToCellData != null, 'Expected a table that accepts files to convert them to cell data');
    const {text, cellData} = await convertFileToCellData(file);
    if (isMounted()) {
      commitCellAndStay(index, text, cellData);
    }
  };

  return {
    paste: (e, options) => {
      if (!isMounted()) {
        return;
      }
      const {locked, table} = getTableProps();
      assert(!locked, 'Table paste handler unexpectedly called while locked');

      if (canAttachFileToSelectedCell()) {
        const filePromise = wyciwyp.extractImageFromPaste(e);
        if (filePromise) {
          filePromise.then((file) => {
            if (file) {
              attachFileToSelectedCell(file as File);
            }
          });
          return;
        }
      }

      e.preventDefault();

      // Use the clipboard's HTML when it holds a single table; otherwise build the table from the
      // plain text's newline and tab delimiters.
      const parts = wyciwyp.processClipboard(e.clipboardData, {
        allowTables: true,
        ignoreTableMinWidth: true,
        ignoreTableMinHeight: true,
        tableType: table._tableType,
      });
      const cells: Array<Array<PastedCell>> =
        parts.length === 1 && parts[0].type === 'table'
          ? (parts[0] as TableWYCIWYPPart).text.table
          : parseRawDataToCells(e.clipboardData.getData('text/plain'), table._tableType);

      const {mutations} = getTableModel();
      mutations.saveSelection();
      mutations.pasteTable(
        cells,
        {
          transpose: options?.transpose,
          selectionIndex: options?.selectionIndex,
          preserveDestinationFormatting: options?.preserveDestinationFormatting,
        },
        ({start, end, text}) => {
          tableSelection.selectRange(start, end, {text});
        }
      );
    },
  };
}
